// ---------------------------------------------------------------------------
// SWEEP §4 — THE TRANSLATION LAYER. Pure: no React, no DOM, so a node test
// executes every rule below.
//
// THE ENGLISH SENTENCE IS THE KEY. `t("My shifts")`, not `t("shifts.title")`.
// Three reasons, in the order they matter here:
//
//   1. A MISSING TRANSLATION IS A COMPILE ERROR. `MessageKey` is the key set of
//      the Hungarian catalogue, so a sentence a screen asks for that `hu.ts`
//      does not carry does not typecheck. A second locale is `Record<MessageKey,
//      string>` below, and a sentence it lacks does not typecheck either.
//   2. THE COPY STAYS IN THE SCREEN. verify-V16a, V28 and V37 grep the screen
//      files for sentences the law or a ruling requires a worker to read. A key
//      like `card.body` would move the sentence out of their sight and fail all
//      three; the English key keeps it where they look.
//   3. ENGLISH NEEDS NO CATALOGUE. It is the key, so it cannot drift from itself.
//
// INTERPOLATION IS `{name}`, AND THE NAMES ARE TYPED. A key with slots demands
// an object naming exactly those slots; a key without any takes no second
// argument.
// ---------------------------------------------------------------------------

import { HU } from "./hu";

export const LOCALES = ["en", "hu"] as const;
export type Locale = (typeof LOCALES)[number];

export type MessageKey = keyof typeof HU;

/** The `{slot}` names in a key, as a union. */
type Slots<S> = S extends `${string}{${infer P}}${infer R}` ? P | Slots<R> : never;
export type Args<K extends MessageKey> = [Slots<K>] extends [never] ? [] : [Record<Slots<K>, string | number>];

/** English is null: the key is the sentence. Every other locale carries every key. */
const CATALOGUES: { [L in Locale]: Record<MessageKey, string> | null } = { en: null, hu: HU };

/**
 * The tag Intl is given for dates, times, money and numbers.
 *
 * ENGLISH IS `undefined`, which is the reader's own browser locale — exactly
 * what every formatter did before this layer existed, so an English reader in
 * London keeps 24-hour day-month dates and one in Ohio keeps theirs.
 */
export function intlTag(locale: Locale): string | undefined {
  return locale === "en" ? undefined : locale;
}

export function isLocale(value: unknown): value is Locale {
  return (LOCALES as readonly unknown[]).includes(value);
}

export function translate<K extends MessageKey>(locale: Locale, key: K, ...args: Args<K>): string {
  const text = CATALOGUES[locale]?.[key] ?? key;
  const vars = args[0] as Record<string, string | number> | undefined;
  return vars ? fill(locale, text, vars) : text;
}

/**
 * S145. A `t` a helper in system/ can take. The screen passes its own; the default
 * is the English the helper's tests read, so a caller that has none is unchanged.
 */
export type Translate = <K extends MessageKey>(key: K, ...args: Args<K>) => string;
export const english: Translate = (key, ...args) => translate("en", key, ...args);

// One formatter per locale. Building an Intl.NumberFormat costs far more than using one,
// and S145 put t() on the board's per-cell path: 2,000 cells made it 50x its budget.
const NUMBERS = new Map<Locale, Intl.NumberFormat>();

function fill(locale: Locale, text: string, vars: Record<string, string | number>): string {
  let numbers = NUMBERS.get(locale);
  if (numbers === undefined) NUMBERS.set(locale, (numbers = new Intl.NumberFormat(intlTag(locale))));
  return text.replace(/\{(\w+)\}/g, (slot, name: string) => {
    const value = vars[name];
    if (value === undefined) return slot;
    if (typeof value !== "number") return value;
    // A whole number from 0 to 999 is its own digits in every locale shipped (en, hu: Latin
    // digits, no grouping). Skipping Intl for it is exact (i18n.test.ts compares all 1,000)
    // and keeps the board's per-cell sentences inside V14's budget. Negatives, fractions
    // and anything larger still go through the formatter.
    return Number.isInteger(value) && value >= 0 && value < 1000 ? String(value) : numbers!.format(value);
  });
}

/** S73. What the API says when it refuses (api/src/errors.ts, toErrorBody). Every field optional: it is a wire body. */
export type Refusal = { message?: string; sentence_key?: string; params?: Record<string, string | number> };

/**
 * S73. A refusal from the API, in the reader's language — or its own English when
 * the catalogue does not carry it. NEVER THROWS AND NEVER BLANKS: a sentence the
 * server learned after this build shipped falls back to the server's text, which is
 * still a sentence a person can act on.
 *
 * The key is `sentence_key` when the refusal carries values ("It opens in {minutes}
 * minutes."), else the message itself. A string value that is itself a catalogue
 * key (a field's noun, "language") is worded too. Undefined when there is no
 * refusal, so a screen writes `refusal(body.error) ?? t("its own fallback")`.
 */
export function translateRefusal(locale: Locale, error: Refusal | null | undefined): string | undefined {
  const message = error?.message;
  if (!message) return undefined;
  const catalogue = CATALOGUES[locale] as Record<string, string> | null;
  const text = catalogue?.[error.sentence_key ?? message];
  if (text === undefined) return message;
  if (!error.sentence_key) return text;
  const vars: Record<string, string | number> = {};
  for (const [name, value] of Object.entries(error.params ?? {})) {
    vars[name] = typeof value === "string" ? (catalogue?.[value] ?? value) : value;
  }
  const out = fill(locale, text, vars);
  // A slot the server did not fill would show "{minutes}"; its own English is better.
  return /\{\w+\}/.test(out) ? message : out;
}

/**
 * A label that is DATA rather than a literal — navigation.ts's entries. Not
 * typed against the catalogue, so test/i18n.test.ts asserts every worker label
 * has a translation instead; a missing one would render in English, not blank.
 */
/**
 * A row's name in the reader's language: the translation carried with the row (a credential,
 * a role), else the name it was written in. Names are configuration, so they travel with the
 * data and never live in the catalogue above (absolute rule 4). Tour T-34.
 */
export function localName(locale: Locale, row: { name: string; names?: Record<string, string> }): string {
  return row.names?.[locale] ?? row.name;
}

export function translateLabel(locale: Locale, text: string): string {
  return CATALOGUES[locale]?.[text as MessageKey] ?? text;
}

/**
 * Which language a person reads the app in.
 *
 *   1. Their own choice, stored on their account, when it is one we ship.
 *   2. The organisation's country's language — Intl knows HU speaks hu, so no
 *      country list is written here — when it is one we ship.
 *   3. English.
 *
 * NOT THE BROWSER'S LANGUAGE. A shared phone in a site office is set to
 * whatever its last owner chose; the organisation and the person are facts.
 */
/**
 * S63. The language of a screen shown BEFORE anybody is known — sign-in, the
 * emailed sign-in link, the worker app's loading and no-connection states.
 *
 * THE BROWSER, AND ONLY HERE. resolveLocale() refuses it because a person and an
 * organisation are better facts than a shared phone's last owner; before sign-in
 * neither exists, the browser is the only signal there is, and a wrong guess
 * lasts one screen. The first preferred language we ship wins, else English.
 */
export function browserLocale(preferred: readonly string[] | undefined): Locale {
  for (const tag of preferred ?? []) {
    const language = tag.toLowerCase().split("-")[0];
    if (isLocale(language)) return language;
  }
  return "en";
}

export function resolveLocale(personal: string | null | undefined, country: string | null | undefined): Locale {
  if (isLocale(personal)) return personal;
  if (country) {
    try {
      const spoken = new Intl.Locale("und", { region: country }).maximize().language;
      if (isLocale(spoken)) return spoken;
    } catch {
      // A malformed country code is a malformed payload, and English is the safe answer.
    }
  }
  return "en";
}
